import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { settingsService } from '../../services/settingsService';
import { ratingsService } from '../../services/ratingsService';
import {
  UpdateSettingsRequest,
  RestaurantsSorting,
  RestaurantsRandomSortingType
} from '../../types/settings';
import './Page.css';

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Form state
  const [baseDeliveryPrice, setBaseDeliveryPrice] = useState('');
  const [restaurantPerOrderChargePercentage, setRestaurantPerOrderChargePercentage] = useState('');
  const [driverPerOrderChargePercentage, setDriverPerOrderChargePercentage] = useState('');
  const [clientsSupportChatUrl, setClientsSupportChatUrl] = useState('');
  const [driversSupportChatUrl, setDriversSupportChatUrl] = useState('');
  const [restaurantsSupportChatUrl, setRestaurantsSupportChatUrl] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [shouldDriversConfirmOrderCode, setShouldDriversConfirmOrderCode] = useState(false);
  const [canClientCancelOrder, setCanClientCancelOrder] = useState(false);
  const [restaurantsSortingForClients, setRestaurantsSortingForClients] = useState<RestaurantsSorting>(RestaurantsSorting.CREATED_DESC);
  const [restaurantsRandomSortingType, setRestaurantsRandomSortingType] = useState<RestaurantsRandomSortingType>(
    RestaurantsRandomSortingType.RANDOM
  );
  const [minAndroidVersion, setMinAndroidVersion] = useState('');
  const [minIOSVersion, setMinIOSVersion] = useState('');
  const [usePolygons, setUsePolygons] = useState(false);
  const [allowDriversAcceptOrderWithLowBalance, setAllowDriversAcceptOrderWithLowBalance] = useState(false);
  const [useAutoAcceptOrderRules, setUseAutoAcceptOrderRules] = useState(false);
  const [restaurantsForceClosed, setRestaurantsForceClosed] = useState(false);
  const [bannersEnabled, setBannersEnabled] = useState(false);
  const [restaurantCategoriesEnabled, setRestaurantCategoriesEnabled] = useState(false);
  const [restaurantsMinRatingToShow, setRestaurantsMinRatingToShow] = useState('');
  const [useDriverGroupsForOrderVisibility, setUseDriverGroupsForOrderVisibility] = useState(false);
  const [slotCancellationWindowHours, setSlotCancellationWindowHours] = useState('4');
  const [recalculatingRatings, setRecalculatingRatings] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await settingsService.getSettings();

      if (response.success && response.data) {
        // Populate form fields
        setBaseDeliveryPrice(response.data.baseDeliveryPrice.toString());
        setRestaurantPerOrderChargePercentage(response.data.restaurantPerOrderChargePercentage.toString());
        setDriverPerOrderChargePercentage(response.data.driverPerOrderChargePercentage.toString());
        setClientsSupportChatUrl(response.data.clientsSupportChatUrl);
        setDriversSupportChatUrl(response.data.driversSupportChatUrl);
        setRestaurantsSupportChatUrl(response.data.restaurantsSupportChatUrl);
        setAdminEmail(response.data.adminEmail);
        setShouldDriversConfirmOrderCode(response.data.shouldDriversConfirmOrderCode);
        setCanClientCancelOrder(response.data.canClientCancelOrder);
        setRestaurantsSortingForClients(response.data.restaurantsSortingForClients);
        setRestaurantsRandomSortingType(
          response.data.restaurantsRandomSortingType || RestaurantsRandomSortingType.RANDOM
        );
        setMinAndroidVersion(response.data.minAndroidVersion || '');
        setMinIOSVersion(response.data.minIOSVersion || '');
        setUsePolygons(response.data.usePolygons);
        setAllowDriversAcceptOrderWithLowBalance(response.data.allowDriversAcceptOrderWithLowBalance);
        setUseAutoAcceptOrderRules(response.data.useAutoAcceptOrderRules);
        setRestaurantsForceClosed(response.data.restaurantsForceClosed);
        setBannersEnabled(response.data.bannersEnabled);
        setRestaurantCategoriesEnabled(response.data.isRestaurantCategoriesEnabled);
        setRestaurantsMinRatingToShow(response.data.restaurantsMinRatingToShow.toString());
        setUseDriverGroupsForOrderVisibility(response.data.useDriverGroupsForOrderVisibility);
        setSlotCancellationWindowHours((response.data.slotCancellationWindowHours ?? 4).toString());
      } else {
        setError(response.error || 'Failed to fetch settings');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validation
    const deliveryPrice = parseInt(baseDeliveryPrice);
    const restaurantPercentage = parseInt(restaurantPerOrderChargePercentage);
    const driverPercentage = parseInt(driverPerOrderChargePercentage);
    const minRatingToShow = parseFloat(restaurantsMinRatingToShow);

    if (isNaN(deliveryPrice) || deliveryPrice < 0) {
      setError('Please enter a valid base delivery price');
      return;
    }

    if (isNaN(restaurantPercentage) || restaurantPercentage < 0 || restaurantPercentage > 100) {
      setError('Restaurant charge percentage must be between 0 and 100');
      return;
    }

    if (isNaN(driverPercentage) || driverPercentage < 0 || driverPercentage > 100) {
      setError('Driver charge percentage must be between 0 and 100');
      return;
    }

    if (!adminEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    if (isNaN(minRatingToShow) || minRatingToShow < 0 || minRatingToShow > 5) {
      setError(t('settings.restaurantsMinRatingToShowValidation'));
      return;
    }

    setSaving(true);

    try {
      const request: UpdateSettingsRequest = {
        baseDeliveryPrice: deliveryPrice,
        restaurantPerOrderChargePercentage: restaurantPercentage,
        driverPerOrderChargePercentage: driverPercentage,
        clientsSupportChatUrl: clientsSupportChatUrl.trim(),
        driversSupportChatUrl: driversSupportChatUrl.trim(),
        restaurantsSupportChatUrl: restaurantsSupportChatUrl.trim(),
        adminEmail: adminEmail.trim(),
        shouldDriversConfirmOrderCode: shouldDriversConfirmOrderCode,
        canClientCancelOrder: canClientCancelOrder,
        restaurantsSortingForClients: restaurantsSortingForClients,
        restaurantsRandomSortingType: restaurantsRandomSortingType,
        minAndroidVersion: minAndroidVersion.trim() || null,
        minIOSVersion: minIOSVersion.trim() || null,
        usePolygons: usePolygons,
        allowDriversAcceptOrderWithLowBalance: allowDriversAcceptOrderWithLowBalance,
        useAutoAcceptOrderRules: useAutoAcceptOrderRules,
        restaurantsForceClosed: restaurantsForceClosed,
        bannersEnabled: bannersEnabled,
        isRestaurantCategoriesEnabled: restaurantCategoriesEnabled,
        restaurantsMinRatingToShow: minRatingToShow,
        useDriverGroupsForOrderVisibility: useDriverGroupsForOrderVisibility,
        slotCancellationWindowHours: parseInt(slotCancellationWindowHours) || 4,
      };

      const response = await settingsService.updateSettings(request);

      if (response.success) {
        setSuccessMessage(t('settings.updated'));
        // Refresh settings to show updated values
        await fetchSettings();
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to update settings');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculateRatings = async () => {
    setError('');
    setSuccessMessage('');
    setRecalculatingRatings(true);

    try {
      const response = await ratingsService.recalculateRatings();

      if (response.success) {
        setSuccessMessage(t('settings.recalculateRatingsSuccess'));
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || t('settings.recalculateRatingsError'));
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setRecalculatingRatings(false);
    }
  };

  const handleBackupDatabase = async () => {
    if (!window.confirm(t('settings.backupDatabaseConfirm'))) {
      return;
    }

    setError('');
    setSuccessMessage('');
    setBackingUp(true);

    try {
      const response = await settingsService.backupDatabase();

      if (response.success) {
        setSuccessMessage(t('settings.backupDatabaseSuccess'));
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError(response.error || t('settings.backupDatabaseError'));
      }
    } catch (error) {
      setError(t('settings.backupDatabaseError'));
    } finally {
      setBackingUp(false);
    }
  };

  const isRandomRestaurantsSortingSelected = [
    RestaurantsSorting.RANDOM_IN_3_HRS,
    RestaurantsSorting.RANDOM_IN_6_HRS,
    RestaurantsSorting.RANDOM_IN_12_HRS,
    RestaurantsSorting.RANDOM_IN_24_HRS
  ].includes(restaurantsSortingForClients);

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>{t('settings.title')}</h1>
          <p>{t('settings.subtitle')}</p>
        </div>
        <div className="page-content">
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            {t('settings.loading')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>{t('settings.title')}</h1>
        <p>{t('settings.subtitle')}</p>
      </div>

      <div className="page-content">
        {error && (
          <div className="error-message" style={{ marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            marginBottom: '1.5rem'
          }}>
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Pricing Settings */}
          <div className="detail-card full-width" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>{t('settings.pricingSettings')}</h3>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                <label style={{ fontWeight: 500, margin: 0 }}>
                  {t('settings.baseDeliveryPrice')}
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/delivery-polygons')}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                  }}
                >
                  {t('settings.polygonsButton')}
                </button>
              </div>
              <input
                type="number"
                value={baseDeliveryPrice}
                onChange={(e) => setBaseDeliveryPrice(e.target.value)}
                placeholder="e.g., 10000"
                className="search-input"
                required
                disabled={saving}
                min="0"
                style={{ maxWidth: '300px' }}
              />
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.baseDeliveryPriceHint')}
              </small>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}>
                <input
                  type="checkbox"
                  checked={usePolygons}
                  onChange={(e) => setUsePolygons(e.target.checked)}
                  disabled={saving}
                  style={{
                    width: '20px',
                    height: '20px',
                    marginRight: '0.75rem',
                    cursor: saving ? 'not-allowed' : 'pointer'
                  }}
                />
                {t('settings.usePolygons')}
              </label>
              <small style={{ display: 'block', marginTop: '0.5rem', marginLeft: '1.95rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.usePolygonsHint')}
              </small>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                {t('settings.restaurantCharge')}
              </label>
              <input
                type="number"
                value={restaurantPerOrderChargePercentage}
                onChange={(e) => setRestaurantPerOrderChargePercentage(e.target.value)}
                placeholder="e.g., 15"
                className="search-input"
                required
                disabled={saving}
                min="0"
                max="100"
                style={{ maxWidth: '300px' }}
              />
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.restaurantChargeHint')}
              </small>
            </div>

            <div style={{ marginBottom: '0' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                {t('settings.driverCharge')}
              </label>
              <input
                type="number"
                value={driverPerOrderChargePercentage}
                onChange={(e) => setDriverPerOrderChargePercentage(e.target.value)}
                placeholder="e.g., 10"
                className="search-input"
                required
                disabled={saving}
                min="0"
                max="100"
                style={{ maxWidth: '300px' }}
              />
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.driverChargeHint')}
              </small>
            </div>
          </div>

          {/* Support Chat URLs */}
          <div className="detail-card full-width" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>{t('settings.supportUrls')}</h3>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                {t('settings.clientsSupport')}
              </label>
              <input
                type="url"
                value={clientsSupportChatUrl}
                onChange={(e) => setClientsSupportChatUrl(e.target.value)}
                placeholder={t('settings.clientsSupportPlaceholder')}
                className="search-input"
                required
                disabled={saving}
              />
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.clientsSupportHint')}
              </small>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                {t('settings.driversSupport')}
              </label>
              <input
                type="url"
                value={driversSupportChatUrl}
                onChange={(e) => setDriversSupportChatUrl(e.target.value)}
                placeholder={t('settings.driversSupportPlaceholder')}
                className="search-input"
                required
                disabled={saving}
              />
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.driversSupportHint')}
              </small>
            </div>

            <div style={{ marginBottom: '0' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                {t('settings.restaurantsSupport')}
              </label>
              <input
                type="url"
                value={restaurantsSupportChatUrl}
                onChange={(e) => setRestaurantsSupportChatUrl(e.target.value)}
                placeholder={t('settings.restaurantsSupportPlaceholder')}
                className="search-input"
                required
                disabled={saving}
              />
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.restaurantsSupportHint')}
              </small>
            </div>
          </div>

          {/* Admin Settings */}
          <div className="detail-card full-width" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>{t('settings.adminSettings')}</h3>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                {t('settings.adminEmail')}
              </label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                placeholder={t('settings.adminEmailPlaceholder')}
                className="search-input"
                required
                disabled={saving}
                style={{ maxWidth: '400px' }}
              />
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.adminEmailHint')}
              </small>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}>
                <input
                  type="checkbox"
                  checked={shouldDriversConfirmOrderCode}
                  onChange={(e) => setShouldDriversConfirmOrderCode(e.target.checked)}
                  disabled={saving}
                  style={{
                    width: '20px',
                    height: '20px',
                    marginRight: '0.75rem',
                    cursor: saving ? 'not-allowed' : 'pointer'
                  }}
                />
                {t('settings.driverConfirmationCode')}
              </label>
              <small style={{ display: 'block', marginTop: '0.5rem', marginLeft: '1.95rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.driverConfirmationCodeHint')}
              </small>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}>
                <input
                  type="checkbox"
                  checked={canClientCancelOrder}
                  onChange={(e) => setCanClientCancelOrder(e.target.checked)}
                  disabled={saving}
                  style={{
                    width: '20px',
                    height: '20px',
                    marginRight: '0.75rem',
                    cursor: saving ? 'not-allowed' : 'pointer'
                  }}
                />
                {t('settings.canClientCancelOrder')}
              </label>
              <small style={{ display: 'block', marginTop: '0.5rem', marginLeft: '1.95rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.canClientCancelOrderHint')}
              </small>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}>
                <input
                  type="checkbox"
                  checked={allowDriversAcceptOrderWithLowBalance}
                  onChange={(e) => setAllowDriversAcceptOrderWithLowBalance(e.target.checked)}
                  disabled={saving}
                  style={{
                    width: '20px',
                    height: '20px',
                    marginRight: '0.75rem',
                    cursor: saving ? 'not-allowed' : 'pointer'
                  }}
                />
                {t('settings.allowDriversAcceptOrderWithLowBalance')}
              </label>
              <small style={{ display: 'block', marginTop: '0.5rem', marginLeft: '1.95rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.allowDriversAcceptOrderWithLowBalanceHint')}
              </small>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500,
                  margin: 0
                }}>
                  <input
                    type="checkbox"
                    checked={useDriverGroupsForOrderVisibility}
                    onChange={(e) => setUseDriverGroupsForOrderVisibility(e.target.checked)}
                    disabled={saving}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '0.75rem',
                      cursor: saving ? 'not-allowed' : 'pointer'
                    }}
                  />
                  {t('settings.useDriverGroupsForOrderVisibility')}
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/driver-group-visibility')}
                  style={{
                    padding: '0.4rem 0.8rem',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: 500
                  }}
                >
                  {t('driverGroupVisibility.manageButton')}
                </button>
              </div>
              <small style={{ display: 'block', marginLeft: '1.95rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.useDriverGroupsForOrderVisibilityHint')}
              </small>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}>
                <input
                  type="checkbox"
                  checked={restaurantsForceClosed}
                  onChange={(e) => setRestaurantsForceClosed(e.target.checked)}
                  disabled={saving}
                  style={{
                    width: '20px',
                    height: '20px',
                    marginRight: '0.75rem',
                    cursor: saving ? 'not-allowed' : 'pointer'
                  }}
                />
                {t('settings.restaurantsForceClosedSetting')}
              </label>
              <small style={{ display: 'block', marginTop: '0.5rem', marginLeft: '1.95rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.restaurantsForceClosedSettingHint')}
              </small>
            </div>

            <div style={{ marginBottom: '0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500,
                  margin: 0
                }}>
                  <input
                    type="checkbox"
                    checked={bannersEnabled}
                    onChange={(e) => setBannersEnabled(e.target.checked)}
                    disabled={saving}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '0.75rem',
                      cursor: saving ? 'not-allowed' : 'pointer'
                    }}
                  />
                  {t('settings.bannersEnabled')}
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/banners')}
                  style={{
                    padding: '0.4rem 0.8rem',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: 500
                  }}
                >
                  {t('banners.manageBanners')}
                </button>
              </div>
              <small style={{ display: 'block', marginLeft: '1.95rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.bannersEnabledHint')}
              </small>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500,
                  margin: 0
                }}>
                  <input
                    type="checkbox"
                    checked={restaurantCategoriesEnabled}
                    onChange={(e) => setRestaurantCategoriesEnabled(e.target.checked)}
                    disabled={saving}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '0.75rem',
                      cursor: saving ? 'not-allowed' : 'pointer'
                    }}
                  />
                  {t('settings.restaurantCategoriesEnabled')}
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/restaurant-categories')}
                  style={{
                    padding: '0.4rem 0.8rem',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: 500
                  }}
                >
                  {t('settings.manageRestaurantCategories')}
                </button>
              </div>
              <small style={{ display: 'block', marginLeft: '1.95rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.restaurantCategoriesEnabledHint')}
              </small>
            </div>

            <div style={{ marginBottom: '0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                <label style={{ fontSize: '1rem', fontWeight: 500, margin: 0 }}>
                  {t('settings.restaurantsMinRatingToShow')}
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/reviews')}
                  style={{
                    padding: '0.4rem 0.8rem',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: 500
                  }}
                >
                  {t('settings.viewReviews')}
                </button>
              </div>
              <input
                type="number"
                value={restaurantsMinRatingToShow}
                onChange={(e) => setRestaurantsMinRatingToShow(e.target.value)}
                placeholder={t('settings.restaurantsMinRatingToShowPlaceholder')}
                className="search-input"
                disabled={saving}
                min={0}
                max={5}
                step={0.1}
                style={{ maxWidth: '220px', marginBottom: '0.5rem' }}
              />
              <small style={{ display: 'block', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.restaurantsMinRatingToShowHint')}
              </small>
            </div>
          </div>

          {/* Slot Settings */}
          <div className="detail-card full-width" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>{t('slots.title')}</h3>
            <div style={{ marginBottom: '0' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                {t('slotSettings.slotCancellationWindowHours')}
              </label>
              <input
                type="number"
                value={slotCancellationWindowHours}
                onChange={(e) => setSlotCancellationWindowHours(e.target.value)}
                placeholder="4"
                className="search-input"
                disabled={saving}
                min="1"
                max="24"
                style={{ maxWidth: '200px' }}
              />
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.85rem' }}>
                {t('slotSettings.slotCancellationWindowHoursHint')}
              </small>
            </div>
          </div>

          {/* Database Backup */}
          <div className="detail-card full-width" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>{t('settings.databaseBackup')}</h3>

            <button
              type="button"
              onClick={handleBackupDatabase}
              disabled={saving || backingUp}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: saving || backingUp ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: 600,
                opacity: saving || backingUp ? 0.7 : 1
              }}
            >
              {backingUp ? t('settings.backupDatabaseRunning') : t('settings.backupDatabaseButton')}
            </button>

            <small style={{ display: 'block', marginTop: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
              {t('settings.backupDatabaseHint')}
            </small>
          </div>

          {/* Ratings Maintenance */}
          <div className="detail-card full-width" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>{t('settings.ratingsMaintenance')}</h3>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleRecalculateRatings}
                disabled={saving || recalculatingRatings}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: saving || recalculatingRatings ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 600,
                  opacity: saving || recalculatingRatings ? 0.7 : 1
                }}
              >
                {recalculatingRatings ? t('settings.recalculateRatingsRunning') : t('settings.recalculateRatingsButton')}
              </button>
              <span style={{ color: '#dc3545', fontWeight: 700 }}>
                {t('settings.recalculateRatingsWarning')}
              </span>
            </div>

            <small style={{ display: 'block', marginTop: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
              {t('settings.recalculateRatingsHint')}
            </small>
          </div>

          {/* Auto-Accept Order Rules */}
          <div className="detail-card full-width" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>{t('settings.autoAcceptRules')}</h3>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}>
                <input
                  type="checkbox"
                  checked={useAutoAcceptOrderRules}
                  onChange={(e) => setUseAutoAcceptOrderRules(e.target.checked)}
                  disabled={saving}
                  style={{
                    width: '20px',
                    height: '20px',
                    marginRight: '0.75rem',
                    cursor: saving ? 'not-allowed' : 'pointer'
                  }}
                />
                {t('settings.useAutoAcceptOrderRules')}
              </label>
              <small style={{ display: 'block', marginTop: '0.5rem', marginLeft: '1.95rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.useAutoAcceptOrderRulesHint')}
              </small>
            </div>

            <div>
              <button
                type="button"
                onClick={() => navigate('/auto-accept-rules')}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500,
                }}
              >
                {t('settings.manageAutoAcceptRules')}
              </button>
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.manageAutoAcceptRulesHint')}
              </small>
            </div>
          </div>

          {/* App Minimum Versions */}
          <div className="detail-card full-width" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>{t('settings.appVersions')}</h3>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                {t('settings.minAndroidVersion')}
              </label>
              <input
                type="text"
                value={minAndroidVersion}
                onChange={(e) => setMinAndroidVersion(e.target.value)}
                placeholder={t('settings.minAndroidVersionPlaceholder')}
                className="search-input"
                disabled={saving}
                style={{ maxWidth: '300px' }}
              />
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.minAndroidVersionHint')}
              </small>
            </div>

            <div style={{ marginBottom: '0' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                {t('settings.minIOSVersion')}
              </label>
              <input
                type="text"
                value={minIOSVersion}
                onChange={(e) => setMinIOSVersion(e.target.value)}
                placeholder={t('settings.minIOSVersionPlaceholder')}
                className="search-input"
                disabled={saving}
                style={{ maxWidth: '300px' }}
              />
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.minIOSVersionHint')}
              </small>
            </div>
          </div>

          {/* Restaurant Sorting Settings */}
          <div className="detail-card full-width" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>{t('settings.restaurantsSorting')}</h3>
            <small style={{ display: 'block', marginBottom: '1rem', color: '#666', fontSize: '0.85rem' }}>
              {t('settings.restaurantsSortingHint')}
            </small>

            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.75rem',
              alignItems: 'stretch'
            }}>
              {[
                { value: RestaurantsSorting.CREATED_ASC, label: t('settings.sortingCreatedAsc'), tooltip: t('settings.sortingCreatedAscTooltip') },
                { value: RestaurantsSorting.CREATED_DESC, label: t('settings.sortingCreatedDesc'), tooltip: t('settings.sortingCreatedDescTooltip') },
                { value: RestaurantsSorting.RATING_ASC, label: t('settings.sortingRatingAsc'), tooltip: t('settings.sortingRatingAscTooltip') },
                { value: RestaurantsSorting.RATING_DESC, label: t('settings.sortingRatingDesc'), tooltip: t('settings.sortingRatingDescTooltip') },
                { value: RestaurantsSorting.NAME_ASC, label: t('settings.sortingNameAsc'), tooltip: t('settings.sortingNameAscTooltip') },
                { value: RestaurantsSorting.NAME_DESC, label: t('settings.sortingNameDesc'), tooltip: t('settings.sortingNameDescTooltip') },
                { value: RestaurantsSorting.RANDOM_IN_3_HRS, label: t('settings.sortingRandom3'), tooltip: t('settings.sortingRandom3Tooltip') },
                { value: RestaurantsSorting.RANDOM_IN_6_HRS, label: t('settings.sortingRandom6'), tooltip: t('settings.sortingRandom6Tooltip') },
                { value: RestaurantsSorting.RANDOM_IN_12_HRS, label: t('settings.sortingRandom12'), tooltip: t('settings.sortingRandom12Tooltip') },
                { value: RestaurantsSorting.RANDOM_IN_24_HRS, label: t('settings.sortingRandom24'), tooltip: t('settings.sortingRandom24Tooltip') },
              ].map((option) => (
                <label
                  key={option.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    border: `2px solid ${restaurantsSortingForClients === option.value ? '#007bff' : '#ddd'}`,
                    borderRadius: '6px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    backgroundColor: restaurantsSortingForClients === option.value ? '#e7f3ff' : '#fff',
                    transition: 'all 0.2s ease',
                    minWidth: 'fit-content',
                    opacity: saving ? 0.6 : 1,
                    position: 'relative'
                  }}
                >
                  <input
                    type="radio"
                    name="restaurantsSorting"
                    value={option.value}
                    checked={restaurantsSortingForClients === option.value}
                    onChange={(e) => setRestaurantsSortingForClients(e.target.value as RestaurantsSorting)}
                    disabled={saving}
                    style={{
                      width: '18px',
                      height: '18px',
                      marginRight: '0.5rem',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      accentColor: '#007bff'
                    }}
                  />
                  <span style={{ marginRight: '0.5rem', fontWeight: 500 }}>{option.label}</span>
                  <span
                    className="tooltip-trigger"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: '#6c757d',
                      color: '#fff',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      cursor: 'help',
                      flexShrink: 0,
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.querySelector('.custom-tooltip') as HTMLElement;
                      if (tooltip) tooltip.style.display = 'block';
                    }}
                    onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.querySelector('.custom-tooltip') as HTMLElement;
                      if (tooltip) tooltip.style.display = 'none';
                    }}
                  >
                    ?
                    <span
                      className="custom-tooltip"
                      style={{
                        display: 'none',
                        position: 'absolute',
                        bottom: 'calc(100% + 8px)',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#333',
                        color: '#fff',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        pointerEvents: 'none',
                        minWidth: '200px',
                        maxWidth: '300px',
                        whiteSpace: 'normal',
                        textAlign: 'center',
                        lineHeight: '1.4'
                      }}
                    >
                      {option.tooltip}
                      <span
                        style={{
                          content: '""',
                          position: 'absolute',
                          top: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          borderWidth: '6px',
                          borderStyle: 'solid',
                          borderColor: '#333 transparent transparent transparent'
                        }}
                      />
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {isRandomRestaurantsSortingSelected && (
            <div className="detail-card full-width" style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>{t('settings.restaurantsRandomSortingType')}</h3>
              <small style={{ display: 'block', marginBottom: '1rem', color: '#666', fontSize: '0.85rem' }}>
                {t('settings.restaurantsRandomSortingTypeHint')}
              </small>

              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.75rem',
                alignItems: 'stretch'
              }}>
                {[
                  {
                    value: RestaurantsRandomSortingType.RANDOM,
                    label: t('settings.randomSortingTypeRandom'),
                    tooltip: t('settings.randomSortingTypeRandomTooltip')
                  },
                  {
                    value: RestaurantsRandomSortingType.BY_RATING_PROBABILITIES_0_5,
                    label: t('settings.randomSortingTypeByRating0_5'),
                    tooltip: t('settings.randomSortingTypeByRating0_5Tooltip')
                  },
                  {
                    value: RestaurantsRandomSortingType.BY_RATING_PROBABILITIES_1,
                    label: t('settings.randomSortingTypeByRating1'),
                    tooltip: t('settings.randomSortingTypeByRating1Tooltip')
                  },
                  {
                    value: RestaurantsRandomSortingType.BY_RATING_PROBABILITIES_1_5,
                    label: t('settings.randomSortingTypeByRating1_5'),
                    tooltip: t('settings.randomSortingTypeByRating1_5Tooltip')
                  },
                  {
                    value: RestaurantsRandomSortingType.BY_RATING_PROBABILITIES_2,
                    label: t('settings.randomSortingTypeByRating2'),
                    tooltip: t('settings.randomSortingTypeByRating2Tooltip')
                  },
                  {
                    value: RestaurantsRandomSortingType.BY_RATING_PROBABILITIES_2_5,
                    label: t('settings.randomSortingTypeByRating2_5'),
                    tooltip: t('settings.randomSortingTypeByRating2_5Tooltip')
                  },
                  {
                    value: RestaurantsRandomSortingType.BY_RATING_PROBABILITIES_3,
                    label: t('settings.randomSortingTypeByRating3'),
                    tooltip: t('settings.randomSortingTypeByRating3Tooltip')
                  },
                  {
                    value: RestaurantsRandomSortingType.BY_RATING_PROBABILITIES_3_5,
                    label: t('settings.randomSortingTypeByRating3_5'),
                    tooltip: t('settings.randomSortingTypeByRating3_5Tooltip')
                  },
                  {
                    value: RestaurantsRandomSortingType.BY_RATING_PROBABILITIES_4,
                    label: t('settings.randomSortingTypeByRating4'),
                    tooltip: t('settings.randomSortingTypeByRating4Tooltip')
                  },
                  {
                    value: RestaurantsRandomSortingType.BY_RATING_PROBABILITIES_4_5,
                    label: t('settings.randomSortingTypeByRating4_5'),
                    tooltip: t('settings.randomSortingTypeByRating4_5Tooltip')
                  },
                  {
                    value: RestaurantsRandomSortingType.BY_RATING_PROBABILITIES_5,
                    label: t('settings.randomSortingTypeByRating5'),
                    tooltip: t('settings.randomSortingTypeByRating5Tooltip')
                  },
                ].map((option) => (
                  <label
                    key={option.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.75rem 1rem',
                      border: `2px solid ${restaurantsRandomSortingType === option.value ? '#007bff' : '#ddd'}`,
                      borderRadius: '6px',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      backgroundColor: restaurantsRandomSortingType === option.value ? '#e7f3ff' : '#fff',
                      transition: 'all 0.2s ease',
                      minWidth: 'fit-content',
                      opacity: saving ? 0.6 : 1,
                      position: 'relative'
                    }}
                  >
                    <input
                      type="radio"
                      name="restaurantsRandomSortingType"
                      value={option.value}
                      checked={restaurantsRandomSortingType === option.value}
                      onChange={(e) => setRestaurantsRandomSortingType(e.target.value as RestaurantsRandomSortingType)}
                      disabled={saving}
                      style={{
                        width: '18px',
                        height: '18px',
                        marginRight: '0.5rem',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        accentColor: '#007bff'
                      }}
                    />
                    <span style={{ marginRight: '0.5rem', fontWeight: 500 }}>{option.label}</span>
                    <span
                      className="tooltip-trigger"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        backgroundColor: '#6c757d',
                        color: '#fff',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        cursor: 'help',
                        flexShrink: 0,
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        const tooltip = e.currentTarget.querySelector('.custom-tooltip') as HTMLElement;
                        if (tooltip) tooltip.style.display = 'block';
                      }}
                      onMouseLeave={(e) => {
                        const tooltip = e.currentTarget.querySelector('.custom-tooltip') as HTMLElement;
                        if (tooltip) tooltip.style.display = 'none';
                      }}
                    >
                      ?
                      <span
                        className="custom-tooltip"
                        style={{
                          display: 'none',
                          position: 'absolute',
                          bottom: 'calc(100% + 8px)',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: '#333',
                          color: '#fff',
                          padding: '0.5rem 0.75rem',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          zIndex: 1000,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                          pointerEvents: 'none',
                          minWidth: '200px',
                          maxWidth: '300px',
                          whiteSpace: 'normal',
                          textAlign: 'center',
                          lineHeight: '1.4'
                        }}
                      >
                        {option.tooltip}
                        <span
                          style={{
                            content: '""',
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            borderWidth: '6px',
                            borderStyle: 'solid',
                            borderColor: '#333 transparent transparent transparent'
                          }}
                        />
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="submit"
              className="search-button"
              disabled={saving}
              style={{ padding: '0.75rem 2rem' }}
            >
              {saving ? t('settings.saving') : t('settings.saveButton')}
            </button>
            <button
              type="button"
              onClick={fetchSettings}
              disabled={saving}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1
              }}
            >
              {t('settings.resetButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
